import React, { CSSProperties, FunctionComponent, useEffect, useMemo } from 'react';
import '../productPage/producthome.css';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { Box, Typography } from '@mui/material';
import * as styles from '../productPage/tableComponents/ProductPage_style';
import { useLocation, useNavigate } from 'react-router-dom';
import { renderBoldText } from '../../utils/RenderBoldText';
import { useResponsive } from '../../contexts/ResponsiveContext';
import StyledDropdown from '../careerPage/StyledDropdown';
import LargeScreenBreadcrumbsContainer from './LargeScreenBreadcrumbsContainer';
import {
    decodeSegment,
    joinPathWithKey,
    normalizePath,
    pathsAreEqual,
    splitNormalizedPath,
} from '../../utils/url';

export const BannerStyle = (backgroundImage: string) => ({
    display: 'flex',
    flexDirection: 'column' as 'column',
    backgroundImage: `linear-gradient(rgba(183, 83, 50, 0.8), rgba(183, 83, 50, 0.8)), url(${backgroundImage})`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    width: '100%',
    color: 'white',
});

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
    pageName: string;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, pageName, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`${pageName}-tabpanel-${index}`}
            aria-labelledby={`${pageName}-tab-${index}`}
            {...other}
            style={{ backgroundColor: 'white' }}
        >
            {value === index && <Box>{children}</Box>}
        </div>
    );
}

function a11yProps(index: number, pageName: string) {
    return {
        id: `${pageName}-tab-${index}`,
        'aria-controls': `${pageName}-tabpanel-${index}`,
    };
}

interface ReusableTabsProps {
    pageName: string;
    bannerImage: string | undefined;
    bannerTitle: string;
    bannerDescription: string | React.ReactNode;
    tabLabels: string[];
    tabKeys: string[];
    tabContents: React.ReactNode[];
    breadcrumbs?: React.ReactNode;
    bannerFontStyle?: CSSProperties;
    subBannerFontStyle?: CSSProperties;
    showTabs: boolean;
    equalTabWidth?: boolean;
    backgroundColor?: string;
    isFullWidth?: boolean;
    changeUrl?: boolean; // 新增可選項，預設為 true
    isHtmlAsset?: boolean;
}

const ReusableTabs: FunctionComponent<ReusableTabsProps> = ({
    pageName,
    bannerImage,
    bannerTitle,
    bannerDescription,
    tabLabels,
    tabContents,
    breadcrumbs,
    bannerFontStyle = styles.bannerFont,
    subBannerFontStyle = styles.bannerFont2,
    showTabs,
    equalTabWidth,
    backgroundColor = '#7A7C81',
    isFullWidth,
    changeUrl = true,
    isHtmlAsset = false,
    tabKeys,
}) => {
    const navigate = useNavigate();
    const location = useLocation();

    const pathInfo = useMemo(() => {
        const normalizedPath = normalizePath(location.pathname);
        const segments = splitNormalizedPath(location.pathname);
        const lastSegmentRaw = segments[segments.length - 1] ?? '';
        const lastSegment = decodeSegment(lastSegmentRaw);
        const matchingIndex = tabKeys.findIndex(
            (key) => key.toLowerCase() === lastSegment.toLowerCase()
        );

        return {
            normalizedPath,
            segments,
            matchingIndex,
        };
    }, [location.pathname, tabKeys]);

    const baseUrl = useMemo(() => {
        const baseSegments =
            pathInfo.matchingIndex >= 0 ? pathInfo.segments.slice(0, -1) : pathInfo.segments;

        if (baseSegments.length === 0) {
            return '/';
        }

        const joined = baseSegments.join('/');
        if (joined === '') {
            return '/';
        }
        return joined.startsWith('/') ? joined : `/${joined}`;
    }, [pathInfo.matchingIndex, pathInfo.segments]);

    const currentTabIndex = pathInfo.matchingIndex >= 0 ? pathInfo.matchingIndex : 0;

    const [value, setValue] = React.useState(currentTabIndex);

    useEffect(() => {
        setValue(currentTabIndex);
    }, [currentTabIndex]);

    useEffect(() => {
        if (!changeUrl || tabKeys.length === 0) {
            return;
        }

        if (pathInfo.matchingIndex >= 0) {
            return;
        }

        const firstKey = tabKeys[0];
        if (!firstKey) {
            return;
        }

        const newPath = joinPathWithKey(baseUrl, firstKey);

        if (!pathsAreEqual(newPath, location.pathname)) {
            navigate(newPath, { replace: true });
        }
    }, [changeUrl, tabKeys, baseUrl, pathInfo.matchingIndex, location.pathname, navigate]);

    const handleDropdownSelect = (selectedLabel: string) => {
        const newValue = tabLabels.findIndex((label) => label === selectedLabel);
        if (newValue < 0) {
            return;
        }

        const targetKey = tabKeys[newValue] ?? selectedLabel.replace(/\s+/g, '');
        const newPath = joinPathWithKey(baseUrl, targetKey);

        if (changeUrl && !pathsAreEqual(newPath, location.pathname)) {
            navigate(newPath);
        }
        setValue(newValue);
    };

    // 大螢幕切換 Tabs
    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        const targetKey = tabKeys[newValue];
        if (!targetKey) {
            setValue(newValue);
            return;
        }

        const newPath = joinPathWithKey(baseUrl, targetKey);
        if (changeUrl && !pathsAreEqual(newPath, location.pathname)) {
            navigate(newPath);
        }
        setValue(newValue);
    };

    // RWD 判斷
    const {
        isMaxScreen,
        isScreenXxl,
        isScreenXl,
        isScreenLg,
        isScreenMd,
        isScreenSm,
        isScreenXs,
        responsiveWidth,
        fullScreenWidth,
    } = useResponsive();

    const fullWidth = (() => {
        if (isMaxScreen) return '100%';
        if (isScreenXxl) return '100%';
        if (isScreenXl) return '1125px';
        if (isScreenLg) return '885px';
        if (isScreenMd) return '650px';
        if (isScreenSm) return '422px';
        return '320px';
    })();

    return (
        <div key={location.pathname} style={styles.productPageContainer}>
            {/* 麵包屑 */}
            {breadcrumbs &&
                (isFullWidth && (isMaxScreen || isScreenXxl) ? (
                    <LargeScreenBreadcrumbsContainer>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                justifyContent: 'center',
                            }}
                            data-testid="breadcrumbs-box"
                        >
                            {breadcrumbs}
                        </Box>
                    </LargeScreenBreadcrumbsContainer>
                ) : (
                    <div style={styles.breadCrumbs}>
                        <Box
                            sx={{
                                width: responsiveWidth,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                justifyContent: 'center',
                                margin: 'auto',
                                paddingTop: '0.125rem',
                                paddingBottom: '0.125rem',
                            }}
                            data-testid={'breadcrumbs-box'}
                        >
                            {breadcrumbs}
                        </Box>
                    </div>
                ))}
            {/* Banner */}
            <div style={BannerStyle(bannerImage || '')}>
                <Box
                    sx={{
                        padding: isFullWidth && isScreenXxl ? '2.13rem 3.88rem 3rem' : null,

                        width: isFullWidth ? fullWidth : responsiveWidth,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                        margin: 'auto',
                        minHeight: '180px',
                    }}
                >
                    <h1
                        style={{
                            ...bannerFontStyle,
                            fontSize: isScreenXs ? '22px' : styles.bannerFont.fontSize,
                            marginBottom: isScreenXs ? '6px' : styles.bannerFont.marginBottom,
                            marginTop: '1rem',
                        }}
                    >
                        {renderBoldText(bannerTitle)}
                    </h1>
                    <span
                        style={{
                            ...subBannerFontStyle,
                            fontSize: isScreenXs ? '14px' : styles.bannerFont2.fontSize,
                            marginBottom: '1rem',
                        }}
                    >
                        {bannerDescription}
                    </span>
                </Box>
            </div>

            {/* Tabs or Dropdown */}
            <Box sx={{ width: '100%', backgroundColor }} data-testid={'reuse-outter_box'}>
                {showTabs && (
                    <Box
                        sx={{
                            backgroundColor: '#7A7C81',
                            width: responsiveWidth,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            justifyContent: 'center',
                            margin: 'auto',
                        }}
                        data-testid={'tabs_box'}
                    >
                        {/* 小螢幕 -> 下拉式選單 */}
                        {isScreenSm || isScreenXs ? (
                            <StyledDropdown
                                label={tabLabels[value] ?? ''} // 初始顯示目前的選項
                                options={tabLabels}
                                onSelect={(selectedLabel: string) => {
                                    handleDropdownSelect(selectedLabel);
                                }}
                                width={responsiveWidth}
                                fullPageWidth={fullScreenWidth}
                            />
                        ) : (
                            // 大螢幕 -> Tabs
                            <Tabs
                                variant="fullWidth"
                                TabIndicatorProps={{ hidden: true }}
                                value={value}
                                onChange={handleTabChange}
                                aria-label="product tabs"
                                sx={{
                                    width: '100%',
                                    display: 'flex',
                                }}
                            >
                                {tabLabels.map((label, index) => (
                                    <Tab
                                        key={index}
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <div
                                                    className={'iconHover'}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontSize: 18,
                                                            fontFamily: 'Poppins',
                                                            marginLeft: 2,
                                                            marginRight: 2,
                                                        }}
                                                    >
                                                        {label}
                                                    </Typography>
                                                </div>
                                            </Box>
                                        }
                                        {...a11yProps(index, pageName)}
                                        sx={{
                                            ...(index === 0
                                                ? styles.firstProductTabsStyle('white')
                                                : styles.productTabsStyle('white')),
                                            flex: equalTabWidth ? 1 : 'unset',
                                            minWidth: 0,
                                        }}
                                    />
                                ))}
                            </Tabs>
                        )}
                    </Box>
                )}
                {isScreenSm || isScreenXs ? (
                    isHtmlAsset ? (
                        <Box
                            width={responsiveWidth}
                            sx={{ py: 3, mt: 1, backgroundColor: 'white', margin: 'auto' }}
                        >
                            {tabContents[value]}
                        </Box>
                    ) : (
                        <Box sx={{ mt: 1, backgroundColor: 'white', margin: 'auto' }}>
                            {tabContents[value]}
                        </Box>
                    )
                ) : isHtmlAsset ? (
                    tabContents.map((content, index) => (
                        <TabPanel value={value} index={index} key={index} pageName={pageName}>
                            <Box width={responsiveWidth} sx={{ py: 2, margin: 'auto' }}>
                                {content}
                            </Box>
                        </TabPanel>
                    ))
                ) : (
                    tabContents.map((content, index) => (
                        <TabPanel value={value} index={index} key={index} pageName={pageName}>
                            {content}
                        </TabPanel>
                    ))
                )}
            </Box>
        </div>
    );
};

export default ReusableTabs;
